import { Link } from 'react-router';
import { HowItWorks } from '@/components/home/how-it-works';
import { ClaimHelp } from '@/components/home/claim-help';
import { SEO, FAQSchema, HowToSchema } from '@/components/seo/seo';
import { SmartCTA } from '@/components/ui/smart-cta';
import {
  DFT_RATINGS_SOURCE,
  RATED_ENGLISH_AUTHORITIES,
  getDftRatingStats,
} from '@fixtyer/content';

// Derived from the checked-in DfT dataset so the hero can never drift from /councils.
const DFT_RATINGS = getDftRatingStats();
const RATED_AUTHORITY_COUNT = RATED_ENGLISH_AUTHORITIES.length;

export function HomePage() {
  // FAQs for schema
  const homeFaqs = [
    { question: 'What is Fixtyer?', answer: 'Fixtyer is a self-service document-preparation platform for pothole damage claims in England and Wales. Fixtyer prepares documents using the information customers provide. Customers review, submit and manage their own claims.' },
    { question: 'How much does it cost?', answer: 'The eligibility check is free and the Council Claim Pack is £35 one-time. An £89 Court Pack is planned but is not currently available to purchase. No hidden fees or commission on any compensation you receive.' },
    { question: 'Do you take a percentage of my compensation?', answer: 'No. You pay a flat fee and keep 100% of any compensation you receive.' },
  ];

  // How-to steps for schema
  const howToSteps = [
    { name: 'Check Eligibility', text: 'Answer quick questions for a general indication against common claim criteria.' },
    { name: 'Gather Evidence', text: 'Upload photos of the pothole and damage, plus your repair invoice.' },
    { name: 'Get Claim Pack', text: 'Fixtyer prepares a claim pack with a letter and organised evidence for your review.' },
    { name: 'Submit and Manage Your Claim', text: "You send your claim using Fixtyer's council-specific guide and manage all correspondence yourself." },
  ];
  return (
    <div className="relative">
      <SEO
        title="Pothole Damage Claims: Council Claim Help | Fixtyer"
        description="Claiming for pothole damage in England or Wales? Find the right authority, use free guides and templates, or prepare a £35 claim pack. You submit your own claim."
        canonical="/"
      />
      <FAQSchema faqs={homeFaqs} />
      <HowToSchema
        name="How to Prepare a Pothole Damage Claim in England and Wales"
        description="A step-by-step guide to preparing and submitting your own pothole damage claim to the relevant highway authority."
        steps={howToSteps}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-brand-500 rounded-full animate-pulse" />
                Free check · £35 flat · No commission
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-950 leading-tight">
                Damaged by a{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">
                  pothole?
                </span>
              </h1>
              
              <h2 className="mt-6 text-lg text-surface-800 max-w-xl">
                Claim the repair cost back from the council yourself — and keep 100% of whatever you recover.
              </h2>
              <p className="mt-3 text-surface-700 max-w-xl">
                <Link to="/meet-mac" className="font-semibold text-brand-700 underline hover:text-brand-800">
                  Mac
                </Link>{' '}
                reviews your photos, checks whether the pothole was already reported, and builds a claim pack
                citing the Highways Act. You submit it and manage it. No solicitor, no claims company, no one
                taking a cut.
              </p>
              <p className="mt-3 text-sm text-surface-600 max-w-xl">
                For 2025-26 the{' '}
                <a
                  href={DFT_RATINGS_SOURCE.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline"
                >
                  Department for Transport
                </a>{' '}
                rated {DFT_RATINGS.red} of England&apos;s {RATED_AUTHORITY_COUNT} highway authorities
                RED for road maintenance, and {DFT_RATINGS.amber} AMBER.{' '}
                <Link to="/councils" className="text-brand-600 hover:underline">
                  See how your council rates →
                </Link>
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <SmartCTA
                  guestText="Start your free check"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-accent-500 text-white font-semibold rounded-lg hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25 text-sm"
                  showArrow
                />
                <Link
                  to="/how-it-works"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-white text-brand-700 font-semibold rounded-lg border-2 border-brand-200 hover:border-brand-300 transition-colors text-sm"
                >
                  How it works
                </Link>
                <Link
                  to="/meet-mac"
                  className="inline-flex items-center gap-2 self-start min-h-11 text-brand-700 font-semibold rounded-lg hover:text-brand-800 hover:underline transition-colors text-sm sm:ml-4 sm:self-auto"
                >
                  <img
                    src="/mac-avatar.png"
                    alt=""
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  Meet Mac
                </Link>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3 text-sm text-surface-800">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Free eligibility check, no card needed
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Keep 100% of any payout
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Evidence checked before you submit
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-success-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  £35 one-off — no subscription
                </div>
              </div>

              <p className="mt-6 text-xs text-surface-600 max-w-xl">
                Claim guidance covers England and Wales. DfT council ratings cover England only; Welsh claims use
                the relevant Welsh highway authority&apos;s own records.
              </p>
            </div>

            {/* Hero illustration */}
            <div className="relative lg:pl-12">
              <div className="relative bg-white rounded-3xl shadow-2xl shadow-brand-900/10 p-8 border border-surface-200">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent-100 rounded-full blur-3xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-brand-100 rounded-full blur-3xl" />
                
                <div className="relative space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-brand-900">Evidence Review</p>
                      <p className="text-sm text-surface-800">Upload photos and get clear, practical guidance</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-brand-900">Claim Pack</p>
                      <p className="text-sm text-surface-800">Generate professional, ready-to-submit documents</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-success-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-brand-900">Submit Your Claim</p>
                      <p className="text-sm text-surface-800">You send directly to the body responsible — you're in the driving seat</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom diagonal stripe bar */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* How it Works */}
      <ClaimHelp />
      <HowItWorks />

      <section className="border-y border-brand-200 bg-brand-50 py-14">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-wider text-brand-700">England council evidence</p>
              <h2 className="mt-2 font-display text-3xl font-bold text-brand-950">
                Find your council&apos;s road data
              </h2>
              <p className="mt-3 max-w-2xl text-surface-700">
                Find a council report, compare the 2025-2026 DfT rating bands and open the underlying dataset.
                Ratings are network context and do not establish notice or liability.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link className="rounded-xl bg-brand-700 px-6 py-3 text-center font-semibold text-white hover:bg-brand-800" to="/councils">
                Find your council
              </Link>
              <Link className="rounded-xl border border-brand-300 bg-white px-6 py-3 text-center font-semibold text-brand-700 hover:bg-brand-100" to="/authority-ratings">
                Search all 154 ratings
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-brand-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-lg text-brand-200 max-w-2xl mx-auto">
              Only pay when you're ready to generate your claim pack
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free tier */}
            <div className="bg-brand-900/50 rounded-2xl p-8 border border-brand-800">
              <h3 className="font-display text-xl font-semibold text-white mb-2">
                Eligibility Check
              </h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-display text-4xl font-bold text-white">Free</span>
              </div>
              <p className="text-brand-200 mb-6">
                Find out if your claim meets common eligibility criteria before investing any time
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Quick indication on whether it\'s worth proceeding',
                  'Initial claim assessment',
                  'Responsible authority lookup',
                  'Understand what evidence councils typically expect',
                  'Evidence requirements checklist',
                  'Avoid spending time gathering evidence unnecessarily',
                  'Basic guidance on next steps',
                  'No obligation to continue',
                  'No credit card required',
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-brand-100">
                    <svg className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <SmartCTA
                guestText="Start Free Check"
                className="block w-full text-center px-6 py-3 bg-brand-800 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
              />
            </div>

            {/* Council Pack - Featured */}
            <div className="relative bg-white rounded-2xl p-8 border-2 border-accent-500 shadow-xl shadow-accent-500/20">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent-500 text-white text-sm font-semibold rounded-full">
                AVAILABLE NOW
              </div>
              <h3 className="font-display text-xl font-semibold text-brand-900 mb-2">
                Council Claim Pack
              </h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-display text-4xl font-bold text-brand-950">£35</span>
                <span className="text-surface-800">one-time</span>
              </div>
              <p className="text-surface-800 mb-6">
                Documents, organised evidence and general information for your own council submission
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Professionally formatted claim pack (citing the Highways Act)',
                  'Council-specific submission guide',
                  'Organised evidence bundle',
                  'AI-powered evidence analysis',
                  'Prior reports check (FixMyStreet)',
                  'Know Your Rights briefing (general guidance)',
                  'Follow-up letter templates',
                  'Clear audit trail of your claim submission',
                  'Designed to match typical council claim requirements',
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-surface-800">
                    <svg className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <SmartCTA
                guestText="Get Started — £35"
                className="block w-full text-center px-6 py-3 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors"
              />
            </div>

            {/* Planned Court Pack */}
            <div className="bg-brand-900/50 rounded-2xl p-8 border border-brand-800">
              <div className="mb-4 inline-flex rounded-full bg-brand-800 px-3 py-1 text-xs font-semibold text-brand-200">
                PLANNED
              </div>
              <h3 className="font-display text-xl font-semibold text-white mb-2">
                Court Claim Pack
              </h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-display text-4xl font-bold text-white">£89</span>
                <span className="text-brand-300">one-time</span>
              </div>
              <p className="text-brand-200 mb-6">
                Prepare to escalate to small claims court if your council rejects your claim
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Pre-filled N1 court claim form',
                  'Particulars of claim template',
                  'Court-ready evidence bundle',
                  'Complete court process guide',
                  'What to expect at a small claims hearing',
                  'Hearing preparation checklist',
                  'Plain-English explanation of court paperwork and deadlines',
                  'Designed for self-representation in small claims court',
                  'Helps you present your case clearly and calmly',
                  'No solicitor required',
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-brand-100">
                    <svg className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                disabled
                className="block w-full text-center px-6 py-3 bg-brand-800 text-brand-400 font-semibold rounded-xl cursor-not-allowed"
              >
                Planned — not currently available
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to pursue a pothole damage claim?
          </h2>
          <p className="text-xl text-brand-100 mb-8">
            Start with the free check, then prepare documents and evidence for your own review.
          </p>
          <SmartCTA
            guestText="Start Your Free Claim Check"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 font-semibold rounded-xl hover:bg-brand-50 transition-colors"
            showArrow
          />
        </div>
      </section>
    </div>
  );
}
